import React, { useEffect, useState } from "react";
import { Loader2, Download, Pin, PinOff, Trash2, Wand2, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const KINDS = [
  { key: "flyer", label: "Flyer", hint: "e.g. Ramadan Umrah 2027, Classic package ₹85,000, departs 10 March, call +91 …" },
  { key: "card", label: "Visiting card", hint: "e.g. Name: Rahim Sheikh, Director, phone +91 …, email, office address" },
  { key: "post", label: "Ad post", hint: "e.g. Instagram post: 10,000 off for 10 days only on Classic Umrah" },
];
const QUALITY = [["low", "Fast"], ["medium", "Good"], ["high", "Best"]];

const CreativeTile = ({ c, onChanged }) => {
  const src = `${API}/ai/images/${c.id}.png`;
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="rounded-xl overflow-hidden ring-1 ring-gold/25 bg-black/30" data-testid={`creative-${c.id}`}>
      <img src={src} alt={c.prompt} className="w-full aspect-square object-contain bg-black/40" />
      <div className="p-2 flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wider text-gold flex-1">{c.kind}{c.pinned ? " · all pages" : ""}{c.for_members ? " · members" : ""}</span>
        <button onClick={() => act(() => api.post(`/ai/images/${c.id}/members`, { on: !c.for_members }), c.for_members ? "Hidden from members" : "Members can now download it with their own name")}
          data-testid={`creative-members-${c.id}`} title={c.for_members ? "Stop sharing with members" : "Share with all members"} className={`p-1.5 rounded hover:bg-white/10 ${c.for_members ? "text-[#25D366]" : "text-emerald-100/60"}`}>
          <Users className="w-4 h-4" />
        </button>
        <a href={src} download={`bengal-umrah-house-${c.kind}.png`} data-testid={`creative-download-${c.id}`} className="p-1.5 rounded hover:bg-white/10"><Download className="w-4 h-4" /></a>
        <button onClick={() => act(() => api.post(`/ai/images/${c.id}/pin`, { pinned: !c.pinned }), c.pinned ? "Removed from all pages" : "Now showing on all pages")}
          data-testid={`creative-pin-${c.id}`} title={c.pinned ? "Remove from all pages" : "Show on all pages"} className="p-1.5 rounded hover:bg-white/10 text-gold">
          {c.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>
        <button onClick={() => window.confirm("Delete this design?") && act(() => api.delete(`/ai/images/${c.id}`), "Deleted")} data-testid={`creative-delete-${c.id}`} className="p-1.5 rounded hover:bg-white/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const ImageStudio = () => {
  const [kind, setKind] = useState("flyer");
  const [quality, setQuality] = useState("medium");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [memberSpace, setMemberSpace] = useState(true);
  const [planning, setPlanning] = useState(false);
  const load = () => api.get("/ai/images").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const plan = async () => {
    setPlanning(true);
    try { const r = await api.post("/ai/images/plan", { kind, idea: prompt }, { timeout: 90000 }); setPrompt(r.data.prompt); toast.success("ChatGPT wrote the design plan — edit it if you like, then Create"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setPlanning(false);
  };

  const create = async () => {
    setBusy(true);
    try { await api.post("/ai/images", { kind, prompt, quality, member_space: memberSpace }, { timeout: 180000 }); toast.success("Design ready"); setPrompt(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Card className="glass border-gold/20 p-4 space-y-4" data-testid="image-studio">
      <p className="font-serif font-semibold text-gold">Design studio</p>
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)} data-testid={`studio-kind-${k.key}`}
            className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${kind === k.key ? "bg-gold text-[#0a2e22] border-gold" : "border-gold/30 text-emerald-100/80 hover:border-gold"}`}>{k.label}</button>
        ))}
      </div>
      <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6} placeholder={KINDS.find((k) => k.key === kind).hint} data-testid="studio-prompt" className="bg-card/60" />
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onClick={plan} disabled={planning || busy || !prompt.trim()} data-testid="studio-plan-btn" className="border-gold/40 text-gold gap-1.5">
          {planning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Write with ChatGPT
        </Button>
        <label className="flex items-center gap-2 text-xs text-emerald-100/80">
          <input type="checkbox" checked={memberSpace} onChange={(e) => setMemberSpace(e.target.checked)} data-testid="studio-member-space" /> Leave space for each member's name, number & address
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {QUALITY.map(([q, l]) => (
          <button key={q} onClick={() => setQuality(q)} data-testid={`studio-quality-${q}`}
            className={`rounded-md px-3 py-1 text-xs border ${quality === q ? "border-gold text-gold" : "border-border text-emerald-100/60"}`}>{l}</button>
        ))}
        <Button onClick={create} disabled={busy || !prompt.trim()} data-testid="studio-create-btn" className="ml-auto bg-gold text-[#0a2e22] hover:bg-gold/90 gap-1.5">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating… up to 1 min</> : <><Wand2 className="w-4 h-4" /> Create design</>}
        </Button>
      </div>
      <p className="text-[11px] text-emerald-100/50">Tip: write phone numbers and names exactly. Always check the text on the design before printing. Tap the pin to show a design on all pages. Tap the people icon to share it with all members — they download it with their own name, number and address.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="studio-gallery">
        {items.map((c) => <CreativeTile key={c.id} c={c} onChanged={load} />)}
        {!items.length && <p className="col-span-full text-sm text-emerald-100/50">Your designs will appear here.</p>}
      </div>
    </Card>
  );
};
